import { useId } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

export interface PlatformMarkProps {
  size?: number;
  animated?: boolean;
  sx?: SxProps<Theme>;
}

const INK = '#2A1263';

/**
 * The platform's own mark (console, sign-in on the platform host, fallbacks):
 * a vertical axis crossed by an orbit, with a dot at the center, drawn in the
 * Faísca lime tile it inherits from. Tenants never see it.
 */
export function PlatformMark({ size = 40, animated = false, sx }: PlatformMarkProps) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const tileId = `platform-tile-${uid}`;
  return (
    <Box
      component="svg"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      aria-hidden="true"
      sx={[
        { display: 'block', flexShrink: 0 },
        animated && {
          '& .platform-orbit': {
            transformOrigin: '24px 24px',
            animation: 'platform-spin 2.4s cubic-bezier(0.2, 0, 0, 1) infinite',
          },
          '@keyframes platform-spin': {
            from: { transform: 'rotate(-20deg)' },
            to: { transform: 'rotate(340deg)' },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <defs>
        <linearGradient id={tileId} x1="6" y1="4" x2="42" y2="46" gradientUnits="userSpaceOnUse">
          <stop stopColor="#DDFB6A" />
          <stop offset="1" stopColor="#B4E21C" />
        </linearGradient>
      </defs>
      <rect width="48" height="48" rx="13" fill={`url(#${tileId})`} />
      <path d="M24 10v28" stroke={INK} strokeWidth="3.4" strokeLinecap="round" />
      <ellipse
        className="platform-orbit"
        cx="24"
        cy="24"
        rx="13"
        ry="6"
        transform="rotate(-20 24 24)"
        fill="none"
        stroke={INK}
        strokeWidth="2.6"
      />
      <circle cx="24" cy="24" r="3.6" fill={INK} />
    </Box>
  );
}
